import { useEffect, useMemo, useRef, useState } from 'react'
import { Download, Sparkles, Trash2, Upload } from 'lucide-react'
import { QRCodeCanvas } from 'qrcode.react'
import { findById } from '@/features/spatial-model/graph.js'
import { useSpatialStore } from '@/features/spatial-model/store.js'
import { requestUploadUrl, confirmUpload } from '@/lib/api/assets.js'
import FloorAssetPanel from './FloorAssetPanel.jsx'

export default function WorkspaceInspector() {
  const selection = useSpatialStore((s) => s.selection)
  const graph = useSpatialStore((s) => s.graph)
  const updateNode = useSpatialStore((s) => s.updateNode)
  const updateEdge = useSpatialStore((s) => s.updateEdge)
  const updateAnchor = useSpatialStore((s) => s.updateAnchor)
  const updateDestination = useSpatialStore((s) => s.updateDestination)
  const updateZone = useSpatialStore((s) => s.updateZone)
  const updateBeacon = useSpatialStore((s) => s.updateBeacon)
  const removeNode = useSpatialStore((s) => s.removeNode)
  const removeEdge = useSpatialStore((s) => s.removeEdge)
  const removeAnchor = useSpatialStore((s) => s.removeAnchor)
  const removeDestination = useSpatialStore((s) => s.removeDestination)
  const removeZone = useSpatialStore((s) => s.removeZone)
  const removeBeacon = useSpatialStore((s) => s.removeBeacon)

  const item = useMemo(() => (selection ? findById(graph, selection.kind, selection.id) : null), [selection, graph])

  if (!selection || !item) {
    return (
      <div>
        <div className="panel-header">
          <div className="panel-header__title">Inspector</div>
        </div>
        <FloorAssetPanel />
        <div className="empty-state" style={{ minHeight: 80, marginTop: 8 }}>
          <div>Select an object to view its properties.</div>
        </div>
      </div>
    )
  }

  const update = (patch) => {
    switch (selection.kind) {
      case 'node': return updateNode(selection.id, patch)
      case 'edge': return updateEdge(selection.id, patch)
      case 'anchor': return updateAnchor(selection.id, patch)
      case 'destination': return updateDestination(selection.id, patch)
      case 'zone': return updateZone(selection.id, patch)
      case 'beacon': return updateBeacon(selection.id, patch)
      default: return null
    }
  }

  const remove = () => {
    switch (selection.kind) {
      case 'node': return removeNode(selection.id)
      case 'edge': return removeEdge(selection.id)
      case 'anchor': return removeAnchor(selection.id)
      case 'destination': return removeDestination(selection.id)
      case 'zone': return removeZone(selection.id)
      case 'beacon': return removeBeacon(selection.id)
      default: return null
    }
  }

  return (
    <div>
      <FloorAssetPanel />
      <div className="panel-header" style={{ marginTop: 8 }}>
        <div className="panel-header__title">Inspector · {selection.kind}</div>
        <button
          type="button"
          className="btn btn--sm btn--danger"
          onClick={remove}
          title={`Delete ${selection.kind} (Del)`}
        >
          <Trash2 size={14} /> Delete
        </button>
      </div>

      <div className="inspector__group">
        <div className="inspector__group-title">Identity</div>
        <Field label="ID">
          <input value={item.id} readOnly />
        </Field>
        {'name' in item && (
          <Field label="Name">
            <input value={item.name || ''} onChange={(e) => update({ name: e.target.value })} />
          </Field>
        )}
        {'node_type' in item && (
          <Field label="Type">
            <select value={item.node_type} onChange={(e) => update({ node_type: e.target.value })}>
              {['start', 'hallway', 'intersection', 'turn', 'doorway', 'elevator', 'stair', 'ramp', 'transition', 'checkpoint', 'destination', 'landmark'].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
        )}
        {'anchor_type' in item && (
          <Field label="Anchor type">
            <select value={item.anchor_type} onChange={(e) => update({ anchor_type: e.target.value })}>
              {['qr', 'visual_checkpoint', 'entry', 'elevator', 'stair', 'destination'].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
        )}
        {'edge_type' in item && (
          <Field label="Edge type">
            <select value={item.edge_type} onChange={(e) => update({ edge_type: e.target.value })}>
              {['hallway', 'doorway', 'ramp', 'elevator_transition', 'stair_transition', 'restricted', 'temporary_detour'].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field>
        )}
        {'category' in item && (
          <Field label="Category">
            <input value={item.category || ''} onChange={(e) => update({ category: e.target.value })} />
          </Field>
        )}
        {'zone_type' in item && (
          <Field label="Zone type">
            <input value={item.zone_type || ''} onChange={(e) => update({ zone_type: e.target.value })} />
          </Field>
        )}
        {'external_code' in item && (
          <Field label="QR code">
            <input value={item.external_code || ''} onChange={(e) => update({ external_code: e.target.value })} />
          </Field>
        )}
      </div>

      {('x' in item || 'position_x' in item) && (
        <div className="inspector__group">
          <div className="inspector__group-title">Position (m)</div>
          <NumberField label="X" value={item.x ?? 0} onChange={(v) => update({ x: v })} />
          <NumberField label="Y" value={item.y ?? 0} onChange={(v) => update({ y: v })} />
          <NumberField label="Z" value={item.z ?? 0} onChange={(v) => update({ z: v })} />
        </div>
      )}

      {'rotation_yaw' in item && (
        <div className="inspector__group">
          <div className="inspector__group-title">Pose (deg)</div>
          <NumberField label="Yaw" value={item.rotation_yaw ?? 0} onChange={(v) => update({ rotation_yaw: v })} />
          <NumberField label="Pitch" value={item.rotation_pitch ?? 0} onChange={(v) => update({ rotation_pitch: v })} />
          <NumberField label="Roll" value={item.rotation_roll ?? 0} onChange={(v) => update({ rotation_roll: v })} />
          {'wall_height' in item && (
            <NumberField label="Wall H" value={item.wall_height ?? 1.4} onChange={(v) => update({ wall_height: v })} />
          )}
        </div>
      )}

      {selection.kind === 'edge' && (
        <div className="inspector__group">
          <div className="inspector__group-title">Connection</div>
          <Field label="From"><input value={item.from_node_id} readOnly /></Field>
          <Field label="To"><input value={item.to_node_id} readOnly /></Field>
          <Field label="Distance"><input value={(item.distance ?? 0).toFixed(2) + ' m'} readOnly /></Field>
          <Field label="Direction">
            <select value={item.directionality} onChange={(e) => update({ directionality: e.target.value })}>
              <option value="two_way">two way</option>
              <option value="one_way">one way</option>
            </select>
          </Field>
          <Field label="Accessible">
            <input
              type="checkbox"
              checked={item.accessible !== false}
              onChange={(e) => update({ accessible: e.target.checked })}
            />
          </Field>
        </div>
      )}

      {selection.kind === 'destination' && (
        <div className="inspector__group">
          <div className="inspector__group-title">Linkage</div>
          <Field label="Linked node">
            <select
              value={item.linked_node_id || ''}
              onChange={(e) => update({ linked_node_id: e.target.value || null })}
            >
              <option value="">— none —</option>
              {graph.nodes.map((n) => (
                <option key={n.id} value={n.id}>{n.name || n.id.slice(0, 8)}</option>
              ))}
            </select>
          </Field>
          <Field label="Public">
            <input
              type="checkbox"
              checked={item.is_public !== false}
              onChange={(e) => update({ is_public: e.target.checked })}
            />
          </Field>
        </div>
      )}

      {selection.kind === 'anchor' && (
        <div className="inspector__group">
          <div className="inspector__group-title">Linkage</div>
          <Field label="Linked node">
            <select
              value={item.linked_node_id || ''}
              onChange={(e) => update({ linked_node_id: e.target.value || null })}
            >
              <option value="">— none —</option>
              {graph.nodes.map((n) => (
                <option key={n.id} value={n.id}>{n.name || n.id.slice(0, 8)}</option>
              ))}
            </select>
          </Field>
          <Field label="Status">
            <select value={item.status} onChange={(e) => update({ status: e.target.value })}>
              <option value="planned">planned</option>
              <option value="installed">installed</option>
              <option value="verified">verified</option>
            </select>
          </Field>
        </div>
      )}

      {selection.kind === 'anchor' && item.anchor_type === 'qr' && (
        <PlacardImageGroup item={item} update={update} />
      )}

      {'notes' in item && (
        <div className="inspector__group">
          <div className="inspector__group-title">Notes</div>
          <textarea
            rows={3}
            value={item.notes || ''}
            onChange={(e) => update({ notes: e.target.value })}
            style={{ width: '100%' }}
          />
        </div>
      )}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div className="inspector__field" style={{ padding: '4px 0' }}>
      <label>{label}</label>
      <div>{children}</div>
    </div>
  )
}

// QR_RENDER_PX is the pixel buffer of the hidden QR canvas. ~1024 is plenty
// of resolution for AR Foundation tracking and lets us upscale into a print
// canvas with imageSmoothingDisabled to keep crisp module edges.
const QR_RENDER_PX = 1024
const PRINT_DPI = 300
const INCHES_PER_METER = 39.3701

function defaultExternalCode(item) {
  return `ANCHOR-${(item.id || '').slice(0, 8).toUpperCase() || 'NEW'}`
}

function PlacardImageGroup({ item, update }) {
  const floor = useSpatialStore((s) => s.floor)
  const qrCanvasRef = useRef(null)
  const fileInputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [status, setStatus] = useState(null)

  const externalCode = (item.external_code && item.external_code.trim()) || defaultExternalCode(item)
  const widthM = Number.isFinite(item.placard_physical_width_m)
    ? item.placard_physical_width_m
    : 0.2159

  async function uploadFile(file) {
    const sign = await requestUploadUrl({
      floor_id: item.floor_id,
      asset_type: 'image',
      file_name: file.name,
      mime: file.type || 'image/png',
      size: file.size,
    })
    if (sign.signed_url) {
      const put = await fetch(sign.signed_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'image/png' },
        body: file,
      })
      if (!put.ok) throw new Error(`upload failed: ${put.status}`)
      await confirmUpload({
        pending_id: sign.pending_id,
        storage_key: sign.storage_key,
        size: file.size,
      }).catch(() => {})
    } else if (sign.mode !== 'demo') {
      throw new Error('no signed upload URL returned')
    }
    // Compute the actual printed QR width on a US Letter sheet (clamped to
    // the printable area) and persist it so AR clients have the physical
    // dimension required for image tracking. Admin can override later.
    const desiredQrIn = widthM * INCHES_PER_METER
    const maxQrIn = 8.5 - 0.5 * 2
    const qrIn = Math.min(desiredQrIn, maxQrIn)
    const printedWidthM = Number((qrIn * 0.0254).toFixed(4))

    update({
      placard_image_bucket: sign.bucket,
      placard_image_storage_key: sign.storage_key,
      placard_physical_width_m: printedWidthM,
    })
  }

  function buildPrintableCanvas() {
    const src = qrCanvasRef.current
    if (!src) throw new Error('QR canvas not ready yet — try again in a moment')

    // US Letter, portrait, at 300 DPI.
    const PAGE_W_IN = 8.5
    const PAGE_H_IN = 11
    const SAFE_MARGIN_IN = 0.5

    const W = Math.round(PAGE_W_IN * PRINT_DPI)
    const H = Math.round(PAGE_H_IN * PRINT_DPI)

    const desiredQrIn = widthM * INCHES_PER_METER
    const maxQrIn = PAGE_W_IN - SAFE_MARGIN_IN * 2
    const qrIn = Math.min(desiredQrIn, maxQrIn)
    const clamped = qrIn < desiredQrIn - 0.001
    const qrPx = Math.round(qrIn * PRINT_DPI)

    const canvas = document.createElement('canvas')
    canvas.width = W
    canvas.height = H
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, W, H)

    const qrTopIn = 1.25
    const qrTopPx = Math.round(qrTopIn * PRINT_DPI)
    const qrLeftPx = Math.round((W - qrPx) / 2)

    ctx.imageSmoothingEnabled = false
    ctx.drawImage(src, qrLeftPx, qrTopPx, qrPx, qrPx)

    ctx.textAlign = 'center'

    ctx.fillStyle = '#475569'
    ctx.font = `${Math.round(0.16 * PRINT_DPI)}px sans-serif`
    ctx.fillText('ARrow placard', W / 2, Math.round(0.85 * PRINT_DPI))

    let labelY = qrTopPx + qrPx + Math.round(0.55 * PRINT_DPI)

    ctx.fillStyle = '#0f172a'
    ctx.font = `bold ${Math.round(0.36 * PRINT_DPI)}px sans-serif`
    ctx.fillText(item.name || externalCode, W / 2, labelY)
    labelY += Math.round(0.42 * PRINT_DPI)

    ctx.fillStyle = '#334155'
    ctx.font = `${Math.round(0.22 * PRINT_DPI)}px sans-serif`
    ctx.fillText(externalCode, W / 2, labelY)
    labelY += Math.round(0.34 * PRINT_DPI)

    if (floor?.name) {
      ctx.fillStyle = '#475569'
      ctx.font = `${Math.round(0.18 * PRINT_DPI)}px sans-serif`
      ctx.fillText(floor.name, W / 2, labelY)
      labelY += Math.round(0.3 * PRINT_DPI)
    }

    const footerY = H - Math.round(0.7 * PRINT_DPI)
    ctx.fillStyle = '#94a3b8'
    ctx.font = `${Math.round(0.14 * PRINT_DPI)}px sans-serif`
    ctx.fillText(
      `Print Letter (8.5 × 11 in), portrait, Actual Size / 100% — no fit-to-page.`,
      W / 2,
      footerY,
    )
    ctx.fillText(
      `QR width: ${qrIn.toFixed(2)} in (${(qrIn * 0.0254).toFixed(3)} m)` +
        (clamped ? `  ·  clamped from requested ${desiredQrIn.toFixed(2)} in` : ''),
      W / 2,
      footerY + Math.round(0.2 * PRINT_DPI),
    )

    return canvas
  }

  function canvasToBlob(canvas, mime = 'image/png') {
    return new Promise((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('canvas export failed'))),
        mime,
      ),
    )
  }

  async function generateAndSave() {
    setBusy(true)
    setError(null)
    setStatus(null)
    try {
      if (!item.external_code) update({ external_code: externalCode })
      const src = qrCanvasRef.current
      if (!src) throw new Error('QR canvas not ready yet — try again in a moment')
      const blob = await canvasToBlob(src)
      const file = new File([blob], `placard-${externalCode}.png`, { type: 'image/png' })
      await uploadFile(file)
      setStatus('Placard generated and saved as the AR tracker image.')
    } catch (err) {
      setError(err?.message || 'generation failed')
    } finally {
      setBusy(false)
    }
  }

  // Auto-generate the placard PNG on first inspection of an anchor that has
  // a QR code but no stored placard image yet. The QR canvas is rendered
  // hidden in this component and is ready by the next tick.
  useEffect(() => {
    if (busy) return
    if (item.placard_image_storage_key) return
    if (!externalCode) return
    const t = setTimeout(() => {
      if (!qrCanvasRef.current) return
      generateAndSave().catch(() => {})
    }, 50)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, item.placard_image_storage_key, externalCode])

  async function downloadPrintable() {
    setBusy(true)
    setError(null)
    setStatus(null)
    try {
      const canvas = buildPrintableCanvas()
      const blob = await canvasToBlob(canvas)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `placard-${externalCode}.png`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1500)
      setStatus('Printable PNG downloaded.')
    } catch (err) {
      setError(err?.message || 'download failed')
    } finally {
      setBusy(false)
    }
  }

  async function handleCustomFile(file) {
    if (!file) return
    setBusy(true)
    setError(null)
    setStatus(null)
    try {
      await uploadFile(file)
      setStatus('Custom placard image uploaded.')
    } catch (err) {
      setError(err?.message || 'upload failed')
    } finally {
      setBusy(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const clearStored = () => {
    update({ placard_image_bucket: null, placard_image_storage_key: null })
    setStatus(null)
  }

  return (
    <div className="inspector__group">
      <div className="inspector__group-title">QR placard</div>

      <Field label="Code">
        <input
          value={item.external_code || ''}
          placeholder={externalCode}
          onChange={(e) => update({ external_code: e.target.value })}
        />
      </Field>
      <NumberField
        label="Print width (m)"
        value={widthM}
        onChange={(v) => update({ placard_physical_width_m: v })}
      />

      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          padding: '8px 0',
        }}
      >
        <div
          style={{
            width: 132,
            height: 132,
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 8,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <QRCodeCanvas
            ref={qrCanvasRef}
            value={externalCode}
            size={QR_RENDER_PX}
            level="M"
            includeMargin
            style={{ width: 116, height: 116 }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
          <button
            type="button"
            className="btn btn--sm btn--primary"
            onClick={generateAndSave}
            disabled={busy}
            title="Generate the QR placard from this anchor's code and save it as the AR tracker image"
          >
            <Sparkles size={14} /> Generate &amp; save placard
          </button>
          <button
            type="button"
            className="btn btn--sm"
            onClick={downloadPrintable}
            disabled={busy}
            title="Download a labeled, print-ready PNG sized to the print width above"
          >
            <Download size={14} /> Download printable PNG
          </button>
          {item.placard_image_storage_key && (
            <button
              type="button"
              className="btn btn--sm btn--danger"
              onClick={clearStored}
              disabled={busy}
            >
              <Trash2 size={12} /> Clear stored image
            </button>
          )}
        </div>
      </div>

      <details style={{ fontSize: 12, marginTop: 4 }}>
        <summary className="muted" style={{ cursor: 'pointer' }}>
          Advanced: upload custom image
        </summary>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => handleCustomFile(e.target.files?.[0])}
            disabled={busy}
            style={{ flex: 1 }}
          />
        </div>
      </details>

      {item.placard_image_storage_key && (
        <Field label="Stored key">
          <input value={item.placard_image_storage_key} readOnly />
        </Field>
      )}

      {busy && (
        <div className="inspector__field" style={{ fontSize: 12, opacity: 0.7 }}>
          <Upload size={12} /> Working…
        </div>
      )}
      {status && !error && (
        <div className="inspector__field" style={{ fontSize: 12, color: 'var(--ok, #15803d)' }}>
          {status}
        </div>
      )}
      {error && (
        <div className="inspector__field" style={{ fontSize: 12, color: '#c33' }}>
          {error}
        </div>
      )}
    </div>
  )
}

function NumberField({ label, value, onChange }) {
  return (
    <Field label={label}>
      <input
        type="number"
        step={0.05}
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      />
    </Field>
  )
}
